import React, { useEffect, useRef, useState, useCallback } from "react";
import { AnimatePresence } from "framer-motion";
import { Crown, Users, Music2, Pin } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useSite } from "../context/SiteContext";
import { getVisitorId } from "../lib/analytics";
import { logDebug } from "../lib/log";
import { loadMyReactions } from "./chat/chatUtils";
import { ChatMessageItem } from "./chat/ChatMessageItem";
import { ChatComposer } from "./chat/ChatComposer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const LiveChat = () => {
  const site = useSite();
  const audio = site?.salon_audio;
  const [messages, setMessages] = useState([]);
  const [username, setUsername] = useState(localStorage.getItem("aka_user") || "");
  const [joined, setJoined] = useState(!!localStorage.getItem("aka_user"));
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [online, setOnline] = useState(1);
  const [showPicker, setShowPicker] = useState(false);
  const [myReactions, setMyReactions] = useState(loadMyReactions);
  const [welcome, setWelcome] = useState(null);
  const [pinned, setPinned] = useState(null);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);
  const visitorId = getVisitorId();

  const fetchMessages = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/chat/messages`);
      setMessages(r.data);
    } catch (e) {
      logDebug("chat poll failed", e);
    }
    try {
      const r = await axios.get(`${API}/chat/pinned`);
      setPinned(r.data || null);
    } catch (e) {
      logDebug("pinned poll failed", e);
    }
  }, []);

  const heartbeat = useCallback(async () => {
    try {
      const r = await axios.post(`${API}/chat/heartbeat`, { visitor_id: visitorId });
      setOnline(r.data.online || 1);
    } catch (e) {
      logDebug("heartbeat failed", e);
    }
  }, [visitorId]);

  useEffect(() => {
    fetchMessages();
    const id = setInterval(fetchMessages, 3000);
    return () => clearInterval(id);
  }, [fetchMessages]);

  useEffect(() => {
    heartbeat();
    const id = setInterval(heartbeat, 15000);
    return () => clearInterval(id);
  }, [heartbeat]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  // Personal, localized welcome shown once per visitor on first connection
  useEffect(() => {
    if (joined && !localStorage.getItem("aka_welcomed")) {
      const name = (localStorage.getItem("aka_user") || username || "").trim();
      if (!name) return;
      const isFr = (navigator.language || "fr").toLowerCase().startsWith("fr");
      const message = isFr
        ? `Bienvenue ${name} \u2726 Merci d'\u00eatre l\u00e0 ! Dis-moi, qu'est-ce que tu \u00e9coutes en ce moment ? \uD83C\uDFB6`
        : `Welcome ${name} \u2726 Thanks for being here! Tell me, what are you listening to right now? \uD83C\uDFB6`;
      setWelcome({ id: "welcome-personal", username: "AKALIBE", message, is_artist: true, created_at: new Date().toISOString() });
      localStorage.setItem("aka_welcomed", "1");
    }
  }, [joined, username]);

  const join = (e) => {
    e.preventDefault();
    const name = username.trim();
    if (!name) return;
    localStorage.setItem("aka_user", name);
    setJoined(true);
  };

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    setSending(true);
    setShowPicker(false);
    try {
      const r = await axios.post(`${API}/chat/messages`, {
        username, message: text.trim(), visitor_id: visitorId,
      });
      setMessages((m) => [...m, r.data]);
      setText("");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Envoi impossible";
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const react = async (msgId, emoji) => {
    const key = `${msgId}:${emoji}`;
    try {
      const r = await axios.post(`${API}/chat/messages/${msgId}/react`, {
        emoji, visitor_id: visitorId,
      });
      setMessages((list) =>
        list.map((m) => (m.id === msgId ? { ...m, reactions: r.data.reactions } : m)),
      );
      setMyReactions((prev) => {
        const next = { ...prev };
        if (r.data.reacted) next[key] = true;
        else delete next[key];
        localStorage.setItem("aka_reactions", JSON.stringify(next));
        return next;
      });
    } catch {
      toast.error("R\u00e9action impossible");
    }
  };

  const report = async (msgId) => {
    try {
      await axios.post(`${API}/chat/messages/${msgId}/report`, { visitor_id: visitorId });
      toast.success("Message signal\u00e9 \u2713 Merci, l'\u00e9quipe va v\u00e9rifier.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Signalement impossible");
    }
  };

  const insertEmoji = (emoji) => {
    setText((t) => (t + emoji).slice(0, 500));
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <section id="chat" data-testid="chat-section" className="py-24 md:py-32 lg:py-40 relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(212,175,55,0.06),transparent_50%)] pointer-events-none" />
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 relative">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-4">
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-gold">Le salon privé</span>
            <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl tracking-tight mt-4 leading-tight">
              Parle avec la <span className="gold-text">communauté</span>.
            </h2>
            <p className="text-[#A3A3A3] font-light mt-6 leading-relaxed max-w-sm text-hover">
              Un espace en direct où les fans échangent et où Akalibe s'exprime sur sa musique. Les messages
              apparaissent en temps réel.
            </p>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-8 text-[#A3A3A3]">
              <span className="flex items-center gap-2">
                <span className="relative flex h-2.5 w-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold opacity-60" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-gold" />
                </span>
                <span className="text-sm tracking-wide">En direct · {messages.length} messages</span>
              </span>
              <span data-testid="chat-online-count" className="flex items-center gap-1.5 text-sm tracking-wide text-gold-light">
                <Users className="w-3.5 h-3.5" />
                {online} en ligne
              </span>
            </div>

            {/* Exclusive audio extract (uploaded via dashboard) */}
            {audio?.url && (
              <div data-testid="salon-audio" className="mt-8 max-w-sm border border-white/[0.08] bg-white/[0.02] overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.08]">
                  <Music2 className="w-4 h-4 text-gold" />
                  <span className="text-sm text-white">Extrait exclusif</span>
                </div>
                <div className="p-4">
                  <audio
                    data-testid="salon-audio-el"
                    controls
                    preload="none"
                    src={`${process.env.REACT_APP_BACKEND_URL}${audio.url}`}
                    className="w-full"
                  >
                    Ton navigateur ne peut pas lire l'audio.
                  </audio>
                  {audio.name && <p className="text-[11px] text-[#8A8A8A] mt-2 truncate">🎧 {audio.name}</p>}
                </div>
              </div>
            )}
          </div>

          {/* Chat panel */}
          <div className="lg:col-span-8 min-w-0">
            <div
              data-testid="chat-panel"
              className="backdrop-blur-2xl bg-white/[0.02] border border-white/[0.08] shadow-[0_8px_40px_rgba(0,0,0,0.5)] flex flex-col h-[70vh] min-h-[440px] sm:h-[560px] w-full max-w-full overflow-hidden"
            >
              <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-4 border-b border-white/[0.08]">
                <div className="flex items-center gap-2 min-w-0">
                  <Users className="w-4 h-4 text-gold shrink-0" />
                  <span className="font-serif text-lg tracking-wide text-white truncate">Salon AKALIBE</span>
                </div>
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#8A8A8A] shrink-0">VIP Room</span>
              </div>

              {/* Pinned message (highlighted by Akalibe) */}
              {pinned && (
                <div data-testid="chat-pinned" className="flex items-start gap-2 px-4 sm:px-6 py-3 border-b border-gold/20 bg-gold/[0.05]">
                  <Pin className="w-3.5 h-3.5 text-gold shrink-0 mt-0.5" fill="currentColor" />
                  <div className="min-w-0">
                    <span className="text-[10px] uppercase tracking-[0.25em] text-gold">Épinglé par Akalibe · {pinned.username}</span>
                    <p className="text-sm text-white/90 break-words">{pinned.message}</p>
                  </div>
                </div>
              )}

              {/* Messages */}
              <div ref={scrollRef} data-testid="chat-messages" className="flex-1 overflow-y-auto px-4 sm:px-6 py-5 space-y-4">
                {welcome && (
                  <div data-testid="chat-welcome" className="flex gap-3">
                    <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5"
                      style={{ background: "linear-gradient(135deg,#D4AF37,#B8860B)", color: "#000" }}>
                      <Crown className="w-4 h-4" fill="currentColor" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="flex items-center gap-1.5 text-sm font-medium text-gold">
                          AKALIBE
                          <span className="text-[9px] uppercase tracking-widest bg-gold text-black px-1.5 py-0.5">Artiste</span>
                        </span>
                        <span className="text-[10px] text-[#8A8A8A]">pour toi</span>
                      </div>
                      <div className="inline-block px-4 py-2.5 max-w-full break-words text-sm leading-relaxed border border-gold/40 bg-gold/[0.06] text-gold-light">
                        {welcome.message}
                      </div>
                    </div>
                  </div>
                )}
                {messages.length === 0 && (
                  <p className="text-[#8A8A8A] text-sm text-center mt-10">
                    Sois le premier à écrire dans le salon…
                  </p>
                )}
                <AnimatePresence initial={false}>
                  {messages.map((m) => (
                    <ChatMessageItem
                      key={m.id}
                      m={m}
                      myReactions={myReactions}
                      onReact={react}
                      onReport={report}
                    />
                  ))}
                </AnimatePresence>
              </div>

              {/* Composer */}
              <ChatComposer
                joined={joined}
                username={username}
                setUsername={setUsername}
                onJoin={join}
                text={text}
                setText={setText}
                onSend={send}
                sending={sending}
                showPicker={showPicker}
                setShowPicker={setShowPicker}
                onInsertEmoji={insertEmoji}
                inputRef={inputRef}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
